import posthog from "posthog-js"
import type { PostHog, PostHogInterface } from "posthog-js"

type PostHogAuthSession = {
	_id?: unknown
	role?: string
	user?: {
		email?: string | null
		name?: string | null
	}
}

export default defineNuxtPlugin((nuxtApp) => {
	const runtimeConfig = useRuntimeConfig()
	const { publicKey, host } = runtimeConfig.public.posthog

	if (!publicKey) {
		if (import.meta.dev) {
			throw new Error(
				"NUXT_PUBLIC_POSTHOG_PROJECT_TOKEN variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once NUXT_PUBLIC_POSTHOG_PROJECT_TOKEN is configured",
			)
		}
		return
	}

	if (!host) {
		if (import.meta.dev) {
			throw new Error(
				"NUXT_PUBLIC_POSTHOG_HOST variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once NUXT_PUBLIC_POSTHOG_HOST is configured",
			)
		}
		return
	}

	const posthogClient = posthog.init(publicKey, {
		api_host: host,
		autocapture: false,
		tracing_headers: [window.location.hostname],
		loaded: (client: PostHogInterface) => {
			if (import.meta.dev) client.debug()
		},
	})

	const { data: auth, status } = useAuth()
	let identifiedUserId: string | undefined

	watch(
		[auth, status],
		([user, authStatus]) => {
			const session = user as PostHogAuthSession | null
			if (authStatus !== "authenticated" || !session?._id) {
				identifiedUserId = undefined
				return
			}

			const userId = String(session._id)
			if (identifiedUserId === userId) return
			if (identifiedUserId) posthogClient.reset()

			const personProperties: Record<string, string> = {
				role: session.role || "unknown",
			}
			if (session.user?.email) personProperties.email = session.user.email
			if (session.user?.name) personProperties.name = session.user.name

			posthogClient.identify(userId, personProperties)
			identifiedUserId = userId
		},
		{ immediate: true },
	)

	nuxtApp.hook("vue:error", (error) => {
		posthogClient.captureException(error)
	})

	return {
		provide: {
			posthog: posthogClient as PostHog,
		},
	}
})
